'use client';
// TODO: Fix React 19 compiler lint errors. See https://react.dev/learn/react-compiler
/* eslint-disable react-hooks/set-state-in-effect */
import useEscapeClick from '@lib/hooks/useEscapeClick';
import useIsScrollTopOfPage from '@lib/hooks/useIsScrollTopOfPage';
import { AnimatePresence, MotionDiv } from '@lib/motion';
import type { MenuSectionType } from '@lib/sanity/queries/menu/types';
import cn from '@utils/cn';
import { isDocumentDefined, isWindowDefined } from '@utils/validations';
import { usePathname } from 'next/navigation';
import { useCallback, useEffect, useRef, useState } from 'react';
import { MenuProps } from './Menu';
import MenuBar, { MenuBarHeight } from './MenuBar';
import MenuSectionDesktop from './MenuSection/MenuSectionDesktop';
import MenuSectionMobile from './MenuSection/MenuSectionMobile';

type MenuClientProps = MenuProps & {
  desktopSections?: React.ReactElement[];
  mobileCategories?: React.ReactElement[];
  renderBrand?: React.ReactNode;
};

export default function MenuClient({
  data,
  firstModuleTheme,
  desktopSections,
  mobileCategories,
  renderBrand,
}: MenuClientProps) {
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const [isBurgerClicked, setIsBurgerClicked] = useState(false);
  const [selectedSection, setSelectedSection] = useState<MenuSectionType | undefined | null>();
  const [isMenuHightlighted, setIsMenuHightlighted] = useState(false);
  const [hoveredSection, setHoveredSection] = useState<MenuSectionType | undefined | null>();
  const [isHoveringOverSection, setIsHoveringOverSection] = useState(false);
  const { isEscapeClicked, setIsEscapeClicked } = useEscapeClick();
  const isScrollTopOfPage = useIsScrollTopOfPage();
  const pathname = usePathname();
  const isBlogRoute = typeof pathname === 'string' && pathname.includes('/blog');
  const [isRouteDesktop, setIsRouteDesktop] = useState(false);

  const changeHoveredSection = useCallback((section?: MenuSectionType | null) => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    timerRef.current = setTimeout(() => {
      setHoveredSection(section);
    }, 50);
  }, []);

  useEffect(() => {
    if (isDocumentDefined) {
      document?.body.classList.toggle('overflow-hidden', isBurgerClicked);
    }
  }, [isBurgerClicked]);

  useEffect(() => {
    if (!isWindowDefined) {
      return;
    }

    function checkWindowSize() {
      const minWidth = isBlogRoute ? 768 : 1024;
      setIsRouteDesktop(window.innerWidth >= minWidth);
    }

    checkWindowSize();
    window.addEventListener('resize', checkWindowSize);
    return () => window.removeEventListener('resize', checkWindowSize);
  }, [isBlogRoute]);

  useEffect(() => {
    if (isRouteDesktop && isBurgerClicked) {
      setIsBurgerClicked(false);
    }
  }, [isRouteDesktop, isBurgerClicked]);

  useEffect(() => {
    const isHighlightMenu =
      !isScrollTopOfPage || !!hoveredSection?.menuCategories?.length || isHoveringOverSection || isBurgerClicked;
    setIsMenuHightlighted(isHighlightMenu);

    if (hoveredSection) {
      setSelectedSection(hoveredSection);
    } else if (!isHoveringOverSection) {
      setSelectedSection(undefined);
    }
  }, [isScrollTopOfPage, hoveredSection, isHoveringOverSection, isBurgerClicked, setIsMenuHightlighted]);

  useEffect(() => {
    if (isEscapeClicked) {
      setHoveredSection(undefined);
      setSelectedSection(undefined);
      setIsEscapeClicked(false);
    }
  }, [isEscapeClicked, setIsEscapeClicked]);

  if (!data?.theme) {
    return;
  }

  return (
    <nav className="fixed z-50 w-full">
      <MenuBar
        data={data}
        theme={isMenuHightlighted ? data.theme : firstModuleTheme}
        setHoveredSection={changeHoveredSection}
        selectedSection={selectedSection}
        isBurgerClicked={isBurgerClicked}
        setIsBurgerClicked={setIsBurgerClicked}
        hideBurger={!data.menuSections?.length}
        renderBrand={renderBrand}
      />
      {!!selectedSection?.menuCategories?.length && (
        <div
          className={cn('hidden', {
            'md:block': isBlogRoute,
            'lg:block': !isBlogRoute,
          })}
        >
          <MenuSectionDesktop
            desktopSections={desktopSections}
            menuSections={data.menuSections}
            selectedSection={selectedSection}
            onMouseEnter={() => setIsHoveringOverSection(true)}
            onMouseLeave={() => setIsHoveringOverSection(false)}
          />
        </div>
      )}
      <div
        className={cn({
          'md:hidden': isBlogRoute,
          'lg:hidden': !isBlogRoute,
          hidden: !isBurgerClicked,
        })}
      >
        <MenuSectionMobile mobileCategories={mobileCategories} data={data} />
      </div>
      <AnimatePresence>
        {(isBurgerClicked || isMenuHightlighted) && (
          <MotionDiv
            className="bg-background absolute top-0 z-10 w-full border-b"
            initial={{ height: 0 }}
            animate={{
              height: !isBurgerClicked ? MenuBarHeight : '100vh',
              transition: { duration: 0.07 },
            }}
            exit={{
              height: 0,
              transition: { delay: 0.1, duration: 0.03 },
            }}
          ></MotionDiv>
        )}
      </AnimatePresence>
    </nav>
  );
}
